import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as sessionsApi from "../api/sessions";

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_sessions");
  const { hasPermission: canEdit } = useViewPermission("edit_sessions");
  const { busy, run } = useMutationLock();

  const fetchSessions = async (pageArg = page) => {
    try {
      const response = await sessionsApi.list({ page: pageArg, limit });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setSessions(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPermission) return;
    fetchSessions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchSessions(newPage);
  };

  const columnKeys = ["id", "startMonth", "endMonth"];

  const [startMonth, setStartMonth] = useState(null);
  const [endMonth, setEndMonth] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
        await sessionsApi.create({
          startMonth,
          endMonth,
        });
        setPage(1);
        await fetchSessions(1);
        setStartMonth(null);
        setEndMonth(null);
        setshowform(false);
        notificationCtx.show({
          message: "Session added successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error adding session:", error);
        notificationCtx.show({
          message: "Failed to add session.",
          stat: "error",
        });
      }
    });
  };

  const handleDeleteSession = (sessionId) => {
    if (!canEdit) return;
    const confirmDeleteSession = async () => {
      try {
        await sessionsApi.remove(sessionId);

        const nextPage =
          sessions.length <= 1 && page > 1 ? page - 1 : page;
        setPage(nextPage);
        await fetchSessions(nextPage);
        notificationCtx.show({
          message: "Session deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete session",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this session?",
      stat: "info",
      onConfirm: confirmDeleteSession,
    });
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "PastPaper",
  ];

  const [showform, setshowform] = useState(false);

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            margin: "100px 50px 0",
            overflow: "hidden",
          }}
        >
          {/* ================= TOP HEADER ================= */}
          <div
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "26px 30px",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                marginBottom: "4px",
              }}
            >
              <div>
                <h1
                  className="mb-1"
                  style={{
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  ALL SESSIONS
                </h1>

                <small
                  style={{
                    color: "#625d75",
                  }}
                >
                  Manage academic sessions and their availability
                </small>
              </div>

              {canEdit && (
                <button
                  className="btn"
                  onClick={() => {
                    // Keep your existing form/modal state logic here
                    setshowform(true);
                  }}
                  style={{
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    color: "#403b55",
                    border: "none",
                    padding: "0 15px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "13px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  + Add Session
                </button>
              )}
            </div>
          </div>

          {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

          {/* ================= TABLE / EMPTY STATE ================= */}
          {sessions.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#fff",
                borderRadius: "14px",
                border: "1px solid #eeeaf8",
                boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                padding: "60px 20px",
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No sessions available
              </div>

              <small style={{ marginTop: "5px" }}>
                There are currently no sessions to display.
              </small>
            </div>
          ) : (
            <>
              {/* ================= TABLE ================= */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #eeeaf8",
                  boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  <table
                    className="table align-middle mb-0"
                    style={{
                      minWidth: "800px",
                      width: "100%",
                      background: "#fff",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {columnKeys.map((key) => (
                          <th
                            key={key}
                            style={{
                              backgroundColor: "#d5d1fd",
                              color: "#403b55",
                              fontWeight: "600",
                              padding: "14px 16px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3eb",
                              whiteSpace: "nowrap",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                            }}
                          >
                            {key.toUpperCase()}
                          </th>
                        ))}

                        <th
                          style={{
                            backgroundColor: "#d5d1fd",
                            color: "#403b55",
                            fontWeight: "600",
                            padding: "14px 16px",
                            textAlign: "center",
                            letterSpacing: "0.4px",
                            fontSize: "13px",
                            borderBottom: "1px solid #e6e3eb",
                            whiteSpace: "nowrap",
                            width: "120px",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                          }}
                        >
                          ACTIONS
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sessions.map((item, index) => (
                        <tr key={index}>
                          {columnKeys.map((key) => (
                            <td
                              key={key}
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: "#403b55",
                                borderBottom: "1px solid #f0eef4",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              {item[key]}
                            </td>
                          ))}

                          <td
                            style={{
                              textAlign: "center",
                              padding: "10px 16px",
                              borderBottom: "1px solid #f0eef4",
                            }}
                          >
                            {canEdit && (
                              <button
                                className="btn"
                                onClick={() => handleDeleteSession(item.id)}
                                style={{
                                  backgroundColor: "#f2c4c4",
                                  border: "none",
                                  borderRadius: "8px",
                                  width: "38px",
                                  height: "34px",
                                  padding: 0,
                                }}
                              >
                                <i
                                  className="fa-solid fa-trash"
                                  style={{
                                    fontSize: "14px",
                                    color: "#654343",
                                  }}
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ================= FOOTER ================= */}
              <PaginationFooter
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="sessions"
              />
            </>
          )}

          {/* ================= ADD SESSION MODAL ================= */}

          {canEdit && showform && (
            <div
              className="modal show"
              style={{
                display: "block",
                backgroundColor: "rgba(48,45,69,0.45)",
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{
                  maxWidth: "520px",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    border: "none",
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
                  }}
                >
                  {/* Modal Header */}
                  <div
                    style={{
                      padding: "22px 24px",
                      backgroundColor: "#d5d1fd",
                      borderBottom: "1px solid rgba(80,70,110,0.08)",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4
                          className="mb-1"
                          style={{
                            fontWeight: "700",
                            color: "#302d45",
                          }}
                        >
                          Add Session
                        </h4>

                        <small
                          style={{
                            color: "#625d75",
                          }}
                        >
                          Create a new academic session
                        </small>
                      </div>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setshowform(false)}
                        aria-label="Close"
                      />
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div
                    style={{
                      padding: "24px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <label
                          htmlFor="sessionStart"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Session Start Month
                        </label>

                        <select
                          id="sessionStart"
                          name="start"
                          value={startMonth || ""}
                          onChange={(e) => setStartMonth(e.target.value)}
                          required
                          className="form-select"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                            backgroundColor: "#fff",
                          }}
                        >
                          <option value="" disabled>
                            Select Session Start Month
                          </option>

                          {months.map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-4">
                        <label
                          htmlFor="sessionEnd"
                          className="form-label"
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#403b55",
                          }}
                        >
                          Session End Month
                        </label>

                        <select
                          id="sessionEnd"
                          name="end"
                          value={endMonth || ""}
                          onChange={(e) => setEndMonth(e.target.value)}
                          required
                          className="form-select"
                          style={{
                            border: "1px solid #e5e1ef",
                            borderRadius: "9px",
                            padding: "10px 13px",
                            color: "#403b55",
                            backgroundColor: "#fff",
                          }}
                        >
                          <option value="" disabled>
                            Select Session End Month
                          </option>

                          {months.map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setshowform(false)}
                          style={{
                            backgroundColor: "#f3f1f7",
                            border: "none",
                            color: "#625d75",
                            fontWeight: "600",
                            padding: "9px 18px",
                            borderRadius: "8px",
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          type="submit"
                          className="btn"
                          disabled={busy || !startMonth || !endMonth}
                          style={{
                            backgroundColor:
                              !startMonth || !endMonth ? "#f3f1f7" : "#f2c4c4",
                            border: "none",
                            color:
                              !startMonth || !endMonth ? "#aaa5b5" : "#654343",
                            fontWeight: "600",
                            padding: "9px 20px",
                            borderRadius: "8px",
                          }}
                        >
                          {busy ? "Working..." : "Add Session"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
